<script setup lang="ts">
import PopoverMenu from '@/components/PopoverMenu.vue'
import FormSelect from '@/components/FormSelect.vue'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { Cloud, DatabaseBackup, Download, HardDrive, Mail, Play, Save, Send } from '@lucide/vue'
import { useI18n } from 'vue-i18n'
import AppShell from '@/components/AppShell.vue'
import OidcSettingsDialog from '@/components/OidcSettingsDialog.vue'
import { api } from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import type {
  AdminSmtpUpdate,
  BackupRecord,
  BackupSettingsUpdate,
  BackupTarget,
} from '@/types/monitoring'
import { formatBytes } from '@/utils/format'

type Tab = 'email' | 'backups'

const { t, locale } = useI18n()
const auth = useAuthStore()
const tab = ref<Tab>('email')
const emailSection = ref<'connection' | 'templates'>('connection')
const loading = ref(true)
const savingEmail = ref(false)
const testingEmail = ref(false)
const savingBackup = ref(false)
const creatingBackup = ref(false)
const error = ref('')
const notice = ref('')
const alertTemplateEditor = ref<HTMLTextAreaElement | null>(null)
const recoveryTemplateEditor = ref<HTMLTextAreaElement | null>(null)
const testRecipient = ref('')
let refreshTimer: ReturnType<typeof setInterval> | undefined

const smtp = ref<AdminSmtpUpdate>({
  enabled: false,
  host: '',
  port: 587,
  username: '',
  password: '',
  passwordConfigured: false,
  clearPassword: false,
  senderAddress: 'pulse@example.com',
  security: 'starttls',
  alertTemplateHtml: '',
  recoveryTemplateHtml: '',
})

const backup = ref<BackupSettingsUpdate>({
  scheduleEnabled: false,
  intervalDays: 7,
  target: 'local',
  endpointUrl: '',
  region: 'us-east-1',
  bucketName: '',
  accessKey: '',
  secretKey: '',
  secretKeyConfigured: false,
  clearSecretKey: false,
})
const records = ref<BackupRecord[]>([])
const placeholders = ['node_name', 'alert_reason', 'metric_value', 'time'] as const

function escapeHtml(value: string): string {
  return value.replace(
    /[&<>'"]/g,
    (character) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[character] ||
      character,
  )
}

function renderPreview(template: string, recovered: boolean): string {
  const samples: Record<(typeof placeholders)[number], string> = {
    node_name: t('admin.previewNode'),
    alert_reason: t(recovered ? 'admin.previewRecoveryReason' : 'admin.previewAlertReason'),
    metric_value: recovered ? '42.0%' : '94.2%',
    time: new Intl.DateTimeFormat(locale.value, {
      dateStyle: 'medium',
      timeStyle: 'medium',
    }).format(new Date()),
  }
  return placeholders.reduce(
    (html, name) => html.split(`{{${name}}}`).join(escapeHtml(samples[name])),
    template,
  )
}

const alertPreviewHtml = computed(() => renderPreview(smtp.value.alertTemplateHtml, false))
const recoveryPreviewHtml = computed(() => renderPreview(smtp.value.recoveryTemplateHtml, true))

function selectTarget(target: BackupTarget): void {
  backup.value.target = target
}

function insertPlaceholder(
  name: (typeof placeholders)[number],
  template: 'alert' | 'recovery',
): void {
  const editor = template === 'alert' ? alertTemplateEditor.value : recoveryTemplateEditor.value
  const value = `{{${name}}}`
  const current =
    template === 'alert' ? smtp.value.alertTemplateHtml : smtp.value.recoveryTemplateHtml
  if (!editor) {
    if (template === 'alert') smtp.value.alertTemplateHtml += value
    else smtp.value.recoveryTemplateHtml += value
    return
  }
  const start = editor.selectionStart
  const end = editor.selectionEnd
  const updated = current.slice(0, start) + value + current.slice(end)
  if (template === 'alert') smtp.value.alertTemplateHtml = updated
  else smtp.value.recoveryTemplateHtml = updated
  requestAnimationFrame(() => {
    editor.focus()
    editor.setSelectionRange(start + value.length, start + value.length)
  })
}

function placeholderToken(name: (typeof placeholders)[number]): string {
  return `{{${name}}}`
}

async function refreshBackups(): Promise<void> {
  records.value = await api.backups(auth.token)
}

async function load(): Promise<void> {
  loading.value = true
  error.value = ''
  try {
    const [smtpSettings, backupSettings] = await Promise.all([
      api.adminSmtp(auth.token),
      api.backupSettings(auth.token),
    ])
    smtp.value = {
      ...smtpSettings,
      password: '',
      clearPassword: false,
    }
    if (!testRecipient.value) testRecipient.value = smtpSettings.senderAddress
    backup.value = {
      ...backupSettings,
      secretKey: '',
      clearSecretKey: false,
    }
    await refreshBackups()
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : t('errors.loadAdmin')
  } finally {
    loading.value = false
  }
}

async function sendTestEmail(): Promise<void> {
  testingEmail.value = true
  error.value = ''
  notice.value = ''
  try {
    const result = await api.sendAdminSmtpTest(auth.token, smtp.value, testRecipient.value.trim())
    notice.value = t('admin.testEmailSent', { recipient: result.recipient })
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : t('errors.testEmail')
  } finally {
    testingEmail.value = false
  }
}

async function saveEmail(): Promise<void> {
  savingEmail.value = true
  error.value = ''
  notice.value = ''
  try {
    const updated = await api.updateAdminSmtp(auth.token, smtp.value)
    smtp.value = { ...updated, password: '', clearPassword: false }
    notice.value = t('admin.emailSaved')
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : t('errors.saveEmailSystem')
  } finally {
    savingEmail.value = false
  }
}

async function saveBackupSettings(): Promise<void> {
  savingBackup.value = true
  error.value = ''
  notice.value = ''
  try {
    const updated = await api.updateBackupSettings(auth.token, backup.value)
    backup.value = { ...updated, secretKey: '', clearSecretKey: false }
    notice.value = t('admin.backupSettingsSaved')
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : t('errors.saveBackupSettings')
  } finally {
    savingBackup.value = false
  }
}

async function createBackup(): Promise<void> {
  creatingBackup.value = true
  error.value = ''
  notice.value = ''
  try {
    const created = await api.createBackup(auth.token)
    records.value = [created, ...records.value.filter((record) => record.id !== created.id)]
    notice.value = t('admin.backupQueued')
    window.setTimeout(() => void refreshBackups().catch(() => undefined), 1000)
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : t('errors.createBackup')
  } finally {
    creatingBackup.value = false
  }
}

async function download(record: BackupRecord): Promise<void> {
  error.value = ''
  try {
    await api.downloadBackup(auth.token, record)
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : t('errors.downloadBackup')
  }
}

function statusLabel(status: BackupRecord['status']): string {
  return t(`admin.status.${status}`)
}

onMounted(() => {
  void load()
  refreshTimer = setInterval(() => {
    if (records.value.some((record) => ['pending', 'running'].includes(record.status))) {
      void refreshBackups().catch(() => undefined)
    }
  }, 3000)
})

onBeforeUnmount(() => {
  if (refreshTimer) clearInterval(refreshTimer)
})
</script>

<template>
  <AppShell>
    <section class="max-w-6xl">
      <div class="flex items-center justify-between gap-4">
        <h1 class="page-title">{{ $t('design.compactAdmin') }}</h1>
        <OidcSettingsDialog />
      </div>

      <div class="mt-7 flex border-b border-slate-200 dark:border-slate-700" role="tablist">
        <button
          type="button"
          role="tab"
          class="flex min-h-11 items-center gap-2 border-b-2 px-4 text-sm font-semibold transition"
          :class="
            tab === 'email' ? 'border-moss-600 text-moss-700' : 'border-transparent text-slate-500'
          "
          :aria-selected="tab === 'email'"
          @click="tab = 'email'"
        >
          <Mail class="size-4" aria-hidden="true" />{{ $t('admin.emailTab') }}
        </button>
        <button
          type="button"
          role="tab"
          class="flex min-h-11 items-center gap-2 border-b-2 px-4 text-sm font-semibold transition"
          :class="
            tab === 'backups'
              ? 'border-moss-600 text-moss-700'
              : 'border-transparent text-slate-500'
          "
          :aria-selected="tab === 'backups'"
          @click="tab = 'backups'"
        >
          <DatabaseBackup class="size-4" aria-hidden="true" />{{ $t('admin.backupTab') }}
        </button>
      </div>

      <div v-if="loading" class="mt-8 space-y-4">
        <div class="h-40 animate-pulse rounded-lg bg-slate-200/70"></div>
        <div class="h-80 animate-pulse rounded-lg bg-slate-200/70"></div>
      </div>

      <template v-else>
        <p
          v-if="error"
          role="alert"
          class="mt-5 rounded-md bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
        >
          {{ error }}
        </p>
        <p v-if="notice" role="status" class="mt-5 text-sm font-medium text-moss-700">
          {{ notice }}
        </p>

        <form v-if="tab === 'email'" class="mt-7 space-y-8" @submit.prevent="saveEmail">
          <div class="flex justify-end">
            <FormSelect
              v-model="emailSection"
              :label="$t('admin.emailTab')"
              :options="[
                { value: 'connection', label: $t('admin.smtpTitle') },
                { value: 'templates', label: $t('admin.templateTitle') },
              ]"
            />
          </div>
          <section v-if="emailSection === 'connection'" class="panel p-5 sm:p-6">
            <div class="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 class="font-display text-lg font-bold text-ink">{{ $t('admin.smtpTitle') }}</h2>
                <p class="mt-1 text-xs text-slate-500">{{ $t('admin.smtpSubtitle') }}</p>
              </div>
              <label class="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <input v-model="smtp.enabled" type="checkbox" class="size-4 accent-moss-600" />
                {{ $t('common.enabled') }}
              </label>
            </div>
            <div class="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-[1fr_9rem_1fr]">
              <label class="block">
                <span class="field-label">{{ $t('admin.smtpHost') }}</span>
                <input
                  v-model="smtp.host"
                  class="field"
                  autocomplete="off"
                  placeholder="smtp.example.com"
                />
              </label>
              <label class="block">
                <span class="field-label">{{ $t('admin.smtpPort') }}</span>
                <input v-model.number="smtp.port" class="field" type="number" min="1" max="65535" />
              </label>
              <label class="block">
                <span class="field-label">{{ $t('admin.senderAddress') }}</span>
                <input
                  v-model="smtp.senderAddress"
                  class="field"
                  type="email"
                  autocomplete="email"
                />
              </label>
              <label class="block">
                <span class="field-label">{{ $t('admin.smtpUsername') }}</span>
                <input v-model="smtp.username" class="field" autocomplete="username" />
              </label>
              <label class="block md:col-span-1 xl:col-span-2">
                <span class="field-label">{{ $t('admin.smtpPassword') }}</span>
                <input
                  v-model="smtp.password"
                  class="field"
                  type="password"
                  autocomplete="new-password"
                  :placeholder="smtp.passwordConfigured ? $t('admin.secretStored') : ''"
                />
              </label>
              <label class="block">
                <span class="field-label">{{ $t('admin.connectionSecurity') }}</span>
                <FormSelect
                  v-model="smtp.security"
                  :label="$t('admin.connectionSecurity')"
                  :options="[
                    { value: 'ssl', label: $t('admin.implicitTls') },
                    { value: 'starttls', label: $t('admin.starttls') },
                    { value: 'none', label: $t('admin.noEncryption') },
                  ]"
                />
              </label>
              <label
                v-if="smtp.passwordConfigured"
                class="flex items-center gap-2 text-sm text-slate-500"
              >
                <input
                  v-model="smtp.clearPassword"
                  type="checkbox"
                  class="size-4 accent-coral-600"
                />
                {{ $t('admin.clearStoredPassword') }}
              </label>
              <label class="block md:col-span-2 xl:col-span-3">
                <span class="field-label">{{ $t('admin.testRecipient') }}</span>
                <input
                  v-model="testRecipient"
                  class="field max-w-lg"
                  type="email"
                  autocomplete="email"
                  :placeholder="$t('admin.testRecipientPlaceholder')"
                />
                <span class="mt-2 block text-xs text-slate-500">{{
                  $t('admin.testRecipientHint')
                }}</span>
              </label>
            </div>
          </section>

          <section v-else class="panel overflow-hidden">
            <div class="border-b border-slate-200 px-5 py-4 sm:px-6 dark:border-slate-700">
              <h2 class="font-display text-lg font-bold text-ink">
                {{ $t('admin.templateTitle') }}
              </h2>
              <p class="mt-1 text-xs text-slate-500">{{ $t('admin.templateSubtitle') }}</p>
            </div>
            <section class="border-b border-slate-200 dark:border-slate-700">
              <div class="px-5 pt-5 sm:px-6 sm:pt-6">
                <h3 class="font-display font-bold text-ink">{{ $t('admin.alertTemplate') }}</h3>
              </div>
              <div class="grid lg:grid-cols-2">
                <div class="p-5 sm:p-6 lg:border-r lg:border-slate-200 dark:lg:border-slate-700">
                  <PopoverMenu
                    :label="$t('design.placeholders')"
                    :width="230"
                    button-class="btn-quiet mb-4 gap-2"
                    ><template #default="{ close }">
                      <button
                        v-for="placeholder in placeholders"
                        :key="`alert-${placeholder}`"
                        type="button"
                        class="menu-item font-mono"
                        role="menuitem"
                        :title="$t('admin.insertPlaceholder')"
                        @click="
                          () => {
                            insertPlaceholder(placeholder, 'alert')
                            close(false)
                          }
                        "
                      >
                        {{ placeholderToken(placeholder) }}
                      </button>
                    </template></PopoverMenu
                  >
                  <label class="block">
                    <span class="sr-only">{{ $t('admin.alertTemplateEditor') }}</span>
                    <textarea
                      ref="alertTemplateEditor"
                      v-model="smtp.alertTemplateHtml"
                      class="field min-h-72 resize-y font-mono text-xs leading-5"
                      spellcheck="false"
                    ></textarea>
                  </label>
                </div>
                <div class="bg-slate-50/70 p-5 sm:p-6 dark:bg-slate-950/40">
                  <p class="mb-3 text-xs font-semibold uppercase text-slate-500">
                    {{ $t('admin.livePreview') }}
                  </p>
                  <iframe
                    class="h-80 w-full rounded border border-slate-200 bg-white"
                    sandbox=""
                    referrerpolicy="no-referrer"
                    :srcdoc="alertPreviewHtml"
                    :title="$t('admin.alertPreviewTitle')"
                  ></iframe>
                </div>
              </div>
            </section>
            <section>
              <div class="px-5 pt-5 sm:px-6 sm:pt-6">
                <h3 class="font-display font-bold text-ink">{{ $t('admin.recoveryTemplate') }}</h3>
              </div>
              <div class="grid lg:grid-cols-2">
                <div class="p-5 sm:p-6 lg:border-r lg:border-slate-200 dark:lg:border-slate-700">
                  <PopoverMenu
                    :label="$t('design.placeholders')"
                    :width="230"
                    button-class="btn-quiet mb-4 gap-2"
                    ><template #default="{ close }">
                      <button
                        v-for="placeholder in placeholders"
                        :key="`recovery-${placeholder}`"
                        type="button"
                        class="menu-item font-mono"
                        role="menuitem"
                        :title="$t('admin.insertPlaceholder')"
                        @click="
                          () => {
                            insertPlaceholder(placeholder, 'recovery')
                            close(false)
                          }
                        "
                      >
                        {{ placeholderToken(placeholder) }}
                      </button>
                    </template></PopoverMenu
                  >
                  <label class="block">
                    <span class="sr-only">{{ $t('admin.recoveryTemplateEditor') }}</span>
                    <textarea
                      ref="recoveryTemplateEditor"
                      v-model="smtp.recoveryTemplateHtml"
                      class="field min-h-72 resize-y font-mono text-xs leading-5"
                      spellcheck="false"
                    ></textarea>
                  </label>
                </div>
                <div class="bg-slate-50/70 p-5 sm:p-6 dark:bg-slate-950/40">
                  <p class="mb-3 text-xs font-semibold uppercase text-slate-500">
                    {{ $t('admin.livePreview') }}
                  </p>
                  <iframe
                    class="h-80 w-full rounded border border-slate-200 bg-white"
                    sandbox=""
                    referrerpolicy="no-referrer"
                    :srcdoc="recoveryPreviewHtml"
                    :title="$t('admin.recoveryPreviewTitle')"
                  ></iframe>
                </div>
              </div>
            </section>
          </section>

          <div class="flex flex-wrap justify-end gap-3">
            <button
              type="button"
              class="btn-quiet gap-2"
              :disabled="testingEmail || savingEmail || !testRecipient.trim()"
              @click="sendTestEmail"
            >
              <Send class="size-4" aria-hidden="true" />{{
                testingEmail ? $t('admin.testingEmail') : $t('admin.sendTestEmail')
              }}
            </button>
            <button type="submit" class="btn-primary gap-2" :disabled="savingEmail || testingEmail">
              <Save class="size-4" aria-hidden="true" />{{
                savingEmail ? $t('common.saving') : $t('admin.saveEmail')
              }}
            </button>
          </div>
        </form>

        <div v-else class="mt-7 space-y-8">
          <section
            class="panel flex flex-col justify-between gap-5 p-5 sm:flex-row sm:items-center sm:p-6"
          >
            <div>
              <h2 class="font-display text-lg font-bold text-ink">
                {{ $t('admin.manualBackup') }}
              </h2>
              <p class="mt-1 text-xs text-slate-500">{{ $t('admin.manualBackupDetail') }}</p>
            </div>
            <button
              type="button"
              class="btn-primary shrink-0 gap-2"
              :disabled="creatingBackup"
              @click="createBackup"
            >
              <Play class="size-4" aria-hidden="true" />{{
                creatingBackup ? $t('admin.startingBackup') : $t('admin.backupNow')
              }}
            </button>
          </section>

          <form class="panel p-5 sm:p-6" @submit.prevent="saveBackupSettings">
            <h2 class="font-display text-lg font-bold text-ink">
              {{ $t('admin.backupConfiguration') }}
            </h2>
            <div class="mt-6 grid gap-6 lg:grid-cols-2">
              <div>
                <label class="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <input
                    v-model="backup.scheduleEnabled"
                    type="checkbox"
                    class="size-4 accent-moss-600"
                  />
                  {{ $t('admin.scheduledBackups') }}
                </label>
                <label class="mt-5 block max-w-xs">
                  <span class="field-label">{{ $t('admin.everyDays') }}</span>
                  <input
                    v-model.number="backup.intervalDays"
                    class="field"
                    type="number"
                    min="1"
                    max="365"
                  />
                </label>
              </div>
              <div>
                <span class="field-label">{{ $t('admin.storageTarget') }}</span>
                <div
                  class="inline-flex rounded-md border border-slate-300 bg-slate-50 p-0.5 dark:border-slate-700 dark:bg-slate-900"
                >
                  <button
                    type="button"
                    class="flex min-h-9 items-center gap-2 rounded-sm px-3 text-sm font-semibold"
                    :class="
                      backup.target === 'local'
                        ? 'bg-white text-ink shadow-sm dark:bg-slate-700'
                        : 'text-slate-500'
                    "
                    @click="selectTarget('local')"
                  >
                    <HardDrive class="size-4" aria-hidden="true" />{{ $t('admin.localBackup') }}
                  </button>
                  <button
                    type="button"
                    class="flex min-h-9 items-center gap-2 rounded-sm px-3 text-sm font-semibold"
                    :class="
                      backup.target === 'remote'
                        ? 'bg-white text-ink shadow-sm dark:bg-slate-700'
                        : 'text-slate-500'
                    "
                    @click="selectTarget('remote')"
                  >
                    <Cloud class="size-4" aria-hidden="true" />{{ $t('admin.remoteBackup') }}
                  </button>
                </div>
              </div>
            </div>

            <div
              v-if="backup.target === 'remote'"
              class="mt-7 grid gap-5 border-t border-slate-200 pt-6 md:grid-cols-2 dark:border-slate-700"
            >
              <label class="block md:col-span-2">
                <span class="field-label">{{ $t('admin.endpointUrl') }}</span>
                <input
                  v-model="backup.endpointUrl"
                  class="field"
                  inputmode="url"
                  placeholder="https://s3.example.com"
                />
              </label>
              <label class="block">
                <span class="field-label">{{ $t('admin.region') }}</span>
                <input v-model="backup.region" class="field" />
              </label>
              <label class="block">
                <span class="field-label">{{ $t('admin.bucketName') }}</span>
                <input v-model="backup.bucketName" class="field" />
              </label>
              <label class="block">
                <span class="field-label">{{ $t('admin.accessKey') }}</span>
                <input v-model="backup.accessKey" class="field" autocomplete="off" />
              </label>
              <label class="block">
                <span class="field-label">{{ $t('admin.secretKey') }}</span>
                <input
                  v-model="backup.secretKey"
                  class="field"
                  type="password"
                  autocomplete="new-password"
                  :placeholder="backup.secretKeyConfigured ? $t('admin.secretStored') : ''"
                />
              </label>
              <label
                v-if="backup.secretKeyConfigured"
                class="flex items-center gap-2 text-sm text-slate-500 md:col-span-2"
              >
                <input
                  v-model="backup.clearSecretKey"
                  type="checkbox"
                  class="size-4 accent-coral-600"
                />
                {{ $t('admin.clearStoredSecret') }}
              </label>
            </div>

            <div class="mt-7 flex justify-end border-t border-slate-200 pt-5 dark:border-slate-700">
              <button type="submit" class="btn-primary gap-2" :disabled="savingBackup">
                <Save class="size-4" aria-hidden="true" />{{
                  savingBackup ? $t('common.saving') : $t('admin.saveBackup')
                }}
              </button>
            </div>
          </form>

          <section class="panel overflow-hidden">
            <div class="border-b border-slate-200 px-5 py-4 sm:px-6 dark:border-slate-700">
              <h2 class="font-display text-lg font-bold text-ink">
                {{ $t('admin.backupHistory') }}
              </h2>
            </div>
            <div v-if="records.length" class="overflow-x-auto">
              <table class="w-full min-w-[760px] text-left text-sm">
                <thead class="bg-slate-50/80 text-xs font-semibold text-slate-500">
                  <tr>
                    <th class="px-5 py-3">{{ $t('admin.file') }}</th>
                    <th class="px-5 py-3">{{ $t('admin.created') }}</th>
                    <th class="px-5 py-3">{{ $t('admin.target') }}</th>
                    <th class="px-5 py-3">{{ $t('admin.size') }}</th>
                    <th class="px-5 py-3">{{ $t('admin.statusLabel') }}</th>
                    <th class="px-5 py-3 text-right">{{ $t('admin.actions') }}</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 dark:divide-slate-700">
                  <tr v-for="record in records" :key="record.id">
                    <td class="px-5 py-4 font-mono text-xs font-medium text-slate-700">
                      {{ record.filename }}
                    </td>
                    <td class="px-5 py-4 text-xs text-slate-500">
                      {{ new Date(record.createdAt).toLocaleString(locale) }}
                    </td>
                    <td class="px-5 py-4 text-xs font-medium text-slate-500">
                      {{
                        $t(record.target === 'remote' ? 'admin.remoteBackup' : 'admin.localBackup')
                      }}
                    </td>
                    <td class="px-5 py-4 font-mono text-xs text-slate-500">
                      {{
                        record.sizeBytes == null
                          ? $t('common.notAvailable')
                          : formatBytes(record.sizeBytes)
                      }}
                    </td>
                    <td class="px-5 py-4">
                      <span
                        class="text-xs font-semibold"
                        :class="
                          record.status === 'failed'
                            ? 'text-coral-600'
                            : record.status === 'completed'
                              ? 'text-moss-700'
                              : 'text-amber-500'
                        "
                        >{{ statusLabel(record.status) }}</span
                      >
                      <p
                        v-if="record.error"
                        class="mt-1 max-w-xs truncate text-xs text-coral-600"
                        :title="record.error"
                      >
                        {{ record.error }}
                      </p>
                    </td>
                    <td class="px-5 py-4 text-right">
                      <button
                        type="button"
                        class="btn-quiet gap-2 !min-h-8 !px-3 !text-xs"
                        :disabled="!record.downloadable"
                        @click="download(record)"
                      >
                        <Download class="size-4" aria-hidden="true" />{{ $t('admin.download') }}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-else class="px-5 py-12 text-center text-sm text-slate-500">
              {{ $t('admin.noBackups') }}
            </p>
          </section>
        </div>
      </template>
    </section>
  </AppShell>
</template>
